import React, { useContext } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { Command } from 'cmdk';
import { AnimatePresence, motion } from 'framer-motion';
import { useFeatureFlagEnabled, usePostHog } from 'posthog-js/react';
import { ThemeContext } from '../ThemeContext';
import caseStudies from '../content/caseStudies';
import { FREELANCE_FEATURE_FLAG } from '../constants/featureFlags';

const CmdkReset = createGlobalStyle`
  [cmdk-root] { all: unset; display: block; }
  [cmdk-input] { all: unset; width: 100%; box-sizing: border-box; }
  [cmdk-list] { all: unset; display: block; }
  [cmdk-group] { all: unset; display: block; }
  [cmdk-group-heading] { all: unset; display: block; }
  [cmdk-item] { all: unset; display: block; cursor: pointer; box-sizing: border-box; }
  [cmdk-empty] { all: unset; display: block; }

  [cmdk-group][data-swatches] > [cmdk-group-items] {
    display: flex !important;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 8px;
    padding: 4px 12px 10px;
    align-items: center;
  }
  [cmdk-group][data-swatches] [cmdk-item] {
    display: inline-flex !important;
    padding: 0 !important;
    width: 36px !important;
    height: 36px !important;
    border-radius: 50% !important;
  }
`;

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 99999;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 18vh;
`;

const PaletteCard = styled(motion.div)`
  width: min(560px, 92vw);
  background: ${p => p.theme === 'light' ? 'rgba(255,255,255,0.95)' : 'rgba(30,30,30,0.95)'};
  backdrop-filter: blur(12px);
  border-radius: 8px;
  border: 1px solid ${p => p.theme === 'light' ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'};
  box-shadow: 0 20px 60px rgba(0,0,0,0.35);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
`;

const SearchIcon = styled.span`
  font-size: 1rem;
  flex-shrink: 0;
  opacity: 0.4;
`;

const SearchInput = styled(Command.Input)`
  flex: 1;
  font-size: 14px;
  background: transparent;
  border: none;
  color: ${p => p.theme === 'light' ? '#111' : '#f0f0f0'};
  outline: none;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;

  &::placeholder { color: ${p => p.theme === 'light' ? '#aaa' : '#555'}; }
`;

const InputDivider = styled.div`
  height: 1px;
  background: ${p => p.theme === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)'};
`;

const ItemList = styled(Command.List)`
  max-height: 340px;
  overflow-y: auto;
  padding: 6px;
`;

const GroupEl = styled(Command.Group)`
  margin-bottom: 4px;
`;

const GroupHeading = styled.div`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${p => p.theme === 'light' ? '#666' : '#aaa'};
  padding: 10px 12px 6px;
`;

const PaletteItem = styled(Command.Item)`
  /* Doubled selector (&&) outranks the [cmdk-item] { all: unset } reset,
     which otherwise strips the flex gap and collapses icon/label/hint. */
  && {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 14px;
  color: ${p => p.theme === 'light' ? '#222' : '#ddd'};
  cursor: pointer;
  user-select: none;

  &[data-selected='true'],
  &:hover {
    background: ${p => p.theme === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)'};
  }
`;

const ItemIcon = styled.span`
  font-size: 1rem;
  width: 22px;
  text-align: center;
  flex-shrink: 0;
`;

const ItemLabel = styled.span`
  flex: 1;
  font-weight: 500;

  [data-selected='true'] & {
    color: var(--accent-color);
  }
`;

const ItemHint = styled.span`
  font-family: 'Monaco', 'Consolas', monospace;
  font-size: 0.8rem;
  color: ${p => p.theme === 'light' ? '#888' : '#808080'};
`;

const Empty = styled(Command.Empty)`
  padding: 32px 16px;
  text-align: center;
  font-size: 0.9rem;
  color: ${p => p.theme === 'light' ? '#888' : '#808080'};
`;

const SwatchPaletteItem = styled(Command.Item)`
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
  padding: 0;

  &[data-selected='true'] > * {
    transform: scale(1.1);
    border-color: ${p => p.theme === 'light' ? '#666' : '#ccc'} !important;
  }
`;

const SwatchCircle = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${p => p.color};
  border: 3px solid ${p => p.$active
    ? (p.theme === 'light' ? '#333' : '#fff')
    : (p.theme === 'light' ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)')
  };
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  transition: transform 0.15s, border-color 0.15s;
`;

const ACCENT_COLORS = [
  { label: 'Orange',  color: '#ff8c00' },
  { label: 'Purple',  color: '#a393f5' },
  { label: 'Blue',    color: '#4cc9f0' },
  { label: 'Pink',    color: '#f72585' },
  { label: 'Green',   color: '#4caf50' },
  { label: 'Crimson', color: '#e91e63' },
];

const SECTIONS = [
  { id: 'home',       label: 'Home',       icon: '🏠', hint: 'README.md' },
  { id: 'about',      label: 'About',      icon: '👤', hint: 'about.md' },
  { id: 'projects',   label: 'Projects',   icon: '📁', hint: 'projects.json' },
  { id: 'experience', label: 'Experience', icon: '📋', hint: 'experience.log' },
  { id: 'contact',    label: 'Contact',    icon: '✉️',  hint: 'contact.txt' },
  { id: 'settings',   label: 'Settings',   icon: '⚙️',  hint: 'settings.ini' },
];

const WORK_WITH_ME_SECTION = { id: 'work-with-me', label: 'Work With Me', icon: '🚀', hint: 'work-with-me.md' };

const CommandPalette = ({ open, onClose, openWindow }) => {
  const { theme, setTheme, accentColor, setAccentColor } = useContext(ThemeContext);
  const posthog = usePostHog();
  const isFreelanceEnabled = useFeatureFlagEnabled(FREELANCE_FEATURE_FLAG) === true;
  const sections = isFreelanceEnabled ? [SECTIONS[0], WORK_WITH_ME_SECTION, ...SECTIONS.slice(1)] : SECTIONS;

  const run = (id, fn) => {
    posthog?.capture('palette_command_selected', { command_id: id });
    fn();
    onClose();
  };

  if (!open) return null;

  return (
    <>
      <CmdkReset />
      <AnimatePresence>
        <Overlay
          key="overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onClick={onClose}
        >
          <PaletteCard
            theme={theme}
            initial={{ opacity: 0, scale: 0.95, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -8 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            style={{ transformOrigin: 'top center' }}
            onClick={e => e.stopPropagation()}
          >
            <Command label="Command palette" loop>
              <SearchRow>
                <SearchIcon>🔍</SearchIcon>
                <SearchInput
                  theme={theme}
                  placeholder="Type a command or search…"
                  autoFocus
                />
              </SearchRow>
              <InputDivider theme={theme} />

              <ItemList>
                <Empty theme={theme}>No results found.</Empty>

                <GroupEl heading={<GroupHeading theme={theme}>Navigate</GroupHeading>}>
                  {sections.map(s => (
                    <PaletteItem
                      key={s.id}
                      theme={theme}
                      value={`${s.label} ${s.hint}`}
                      keywords={[s.id, 'open', 'navigate']}
                      onSelect={() => run(`open:${s.id}`, () => openWindow(s.id))}
                    >
                      <ItemIcon>{s.icon}</ItemIcon>
                      <ItemLabel>Open {s.label}</ItemLabel>
                      <ItemHint theme={theme}>{s.hint}</ItemHint>
                    </PaletteItem>
                  ))}
                </GroupEl>

                {caseStudies.length > 0 && (
                  <GroupEl heading={<GroupHeading theme={theme}>Case Studies</GroupHeading>}>
                    {caseStudies.map(cs => (
                      <PaletteItem
                        key={cs.slug}
                        theme={theme}
                        value={`${cs.title} case study ${cs.summary}`}
                        keywords={[cs.slug, 'project', 'read', 'case study']}
                        onSelect={() => run(`case-study:${cs.slug}`, () => openWindow(`case-study:${cs.slug}`))}
                      >
                        <ItemIcon>📝</ItemIcon>
                        <ItemLabel>{cs.title}</ItemLabel>
                        <ItemHint theme={theme}>case study</ItemHint>
                      </PaletteItem>
                    ))}
                  </GroupEl>
                )}

                <GroupEl heading={<GroupHeading theme={theme}>Appearance</GroupHeading>}>
                  <PaletteItem
                    theme={theme}
                    value="toggle theme dark light"
                    keywords={['theme', 'dark', 'light', 'mode']}
                    onSelect={() => run('toggle:theme', () => setTheme(theme === 'light' ? 'dark' : 'light'))}
                  >
                    <ItemIcon>{theme === 'light' ? '🌙' : '☀️'}</ItemIcon>
                    <ItemLabel>Switch to {theme === 'light' ? 'Dark' : 'Light'} Mode</ItemLabel>
                  </PaletteItem>
                </GroupEl>

                <GroupEl
                  data-swatches=""
                  heading={<GroupHeading theme={theme}>Accent Color</GroupHeading>}
                >
                  {ACCENT_COLORS.map(a => (
                    <SwatchPaletteItem
                      key={a.color}
                      theme={theme}
                      value={`accent color ${a.label}`}
                      keywords={['accent', 'color', a.label.toLowerCase()]}
                      onSelect={() => run(`accent:${a.color}`, () => setAccentColor(a.color))}
                    >
                      <SwatchCircle
                        color={a.color}
                        $active={accentColor === a.color}
                        theme={theme}
                      />
                    </SwatchPaletteItem>
                  ))}
                </GroupEl>

                <GroupEl heading={<GroupHeading theme={theme}>Actions</GroupHeading>}>
                  <PaletteItem
                    theme={theme}
                    value="copy email xanderjennie21"
                    keywords={['copy', 'email', 'contact']}
                    onSelect={() => run('copy:email', () => {
                      navigator.clipboard.writeText('xanderjennie21@gmail.com').catch(() => {});
                    })}
                  >
                    <ItemIcon>📋</ItemIcon>
                    <ItemLabel>Copy Email</ItemLabel>
                    <ItemHint theme={theme}>xanderjennie21@gmail.com</ItemHint>
                  </PaletteItem>
                  <PaletteItem
                    theme={theme}
                    value="open github profile"
                    keywords={['github', 'code', 'profile']}
                    onSelect={() => run('open:github', () => window.open('https://github.com/xanderjennie', '_blank'))}
                  >
                    <ItemIcon>🐙</ItemIcon>
                    <ItemLabel>Open GitHub</ItemLabel>
                  </PaletteItem>
                </GroupEl>
              </ItemList>
            </Command>
          </PaletteCard>
        </Overlay>
      </AnimatePresence>
    </>
  );
};

export default CommandPalette;
